import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  Clock, 
  Shield, 
  CheckCircle,
  Award,
  BarChart3,
  Target,
  Zap,
  Phone,
  Mail,
  Calendar,
  AlertCircle
} from 'lucide-react';

const MedicalBillingSmallPractices = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Helmet>
        <title>Medical Billing for Small Practices | Save 30-50% | MDeRCM</title>
        <meta 
          name="description" 
          content="Medical billing outsourcing for small practices. Reduce costs 30-50%, 98% claim accuracy, HIPAA-compliant. Dedicated support for 1-5 providers." 
        />
        <meta 
          name="keywords" 
          content="medical billing outsourcing for small practices, small practice medical billing, independent physician billing services, medical billing for solo practitioners, small clinic billing outsourcing, affordable medical billing services" 
        />
        <meta property="og:title" content="Medical Billing for Small Practices | Save 30-50% | MDeRCM" />
        <meta property="og:description" content="Expert medical billing for small practices. Reduce costs 30-50%, improve accuracy to 98%." />
        <link rel="canonical" href="https://mdercm.com/medical-billing-outsourcing-small-practices" />
      </Helmet>

      <main style={{ minHeight: '100vh', backgroundColor: '#ffffff', fontFamily: "'Poppins', sans-serif" }}>
        
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
          color: '#ffffff',
          padding: isMobile ? '60px 20px' : '80px 40px'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ maxWidth: '56rem', margin: '0 auto', textAlign: 'center' }}>
              <div style={{
                display: 'inline-block',
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '8px 20px',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '600',
                marginBottom: '20px'
              }}>
                ⭐ #1 Choice for Small Practices
              </div>
              
              <h1 style={{
                fontSize: isMobile ? '2rem' : '3rem',
                fontWeight: 'bold',
                marginBottom: '24px',
                lineHeight: '1.2'
              }}>
                Medical Billing Outsourcing for Small Practices
              </h1>
              
              <p style={{
                fontSize: isMobile ? '1.125rem' : '1.25rem',
                marginBottom: '32px',
                opacity: 0.95,
                lineHeight: '1.75'
              }}>
                Specialized billing solutions for practices with 1-5 providers. Reduce costs by 30-50%, improve collections by 15-25%, and eliminate billing headaches—without hiring expensive in-house staff.
              </p>

              {/* Key Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                gap: '20px',
                marginTop: '40px'
              }}>
                {[
                  { icon: DollarSign, value: '30-50%', label: 'Cost Reduction' },
                  { icon: TrendingUp, value: '15-25%', label: 'Revenue Increase' },
                  { icon: Clock, value: '40%', label: 'Faster Payments' },
                  { icon: Shield, value: '98%', label: 'Claim Accuracy' }
                ].map((stat, idx) => (
                  <div key={idx} style={{
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    padding: '20px',
                    borderRadius: '12px',
                    textAlign: 'center'
                  }}>
                    <stat.icon size={32} style={{ marginBottom: '12px' }} />
                    <div style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '4px' }}>
                      {stat.value}
                    </div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '16px',
                justifyContent: 'center',
                marginTop: '40px'
              }}>
                <Link to="/free-trial" style={{
                  textDecoration: 'none',
                  backgroundColor: '#F26522',
                  color: '#ffffff',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'inline-block',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d3541c'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F26522'}
                >
                  Get Free Consultation →
                </Link>
                
                <a href="tel:+15103566069" style={{
                  textDecoration: 'none',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: '#ffffff',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'inline-block',
                  border: '2px solid rgba(255,255,255,0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                >
                  📞 Call: +1 (510) 356-6069
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151',
              marginBottom: '24px'
            }}>
              Running a small medical practice is challenging enough without the constant stress of medical billing. Between seeing patients, managing staff, and handling administrative tasks, billing often becomes an overwhelming burden that consumes valuable time and resources.
            </p>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151',
              marginBottom: '24px'
            }}>
              <strong style={{ color: '#7c3aed' }}>Medical billing outsourcing for small practices</strong> offers a proven solution. Rather than hiring expensive billing staff, investing in costly software, or spending hours managing claims yourself, you can partner with specialized billing experts who handle everything—at a fraction of the cost.
            </p>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151'
            }}>
              Our tailored approach is specifically designed for practices with 1-5 providers. We understand the unique challenges small practices face and deliver solutions that maximize revenue while minimizing costs. With our service, you'll reduce overhead by 30-50%, improve collections by 15-25%, and reclaim hours of your time every week.
            </p>
          </div>
        </section>

        {/* Problems Section */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#faf5ff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '16px',
              color: '#111827'
            }}>
              6 Major Billing Problems Small Practices Face
            </h2>
            <p style={{
              fontSize: '1.125rem',
              textAlign: 'center',
              color: '#6b7280',
              marginBottom: '48px',
              maxWidth: '48rem',
              margin: '0 auto 48px auto'
            }}>
              These challenges drain time, money, and energy from your practice
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '24px'
            }}>
              {[
                {
                  title: '1. Overwhelming Billing Costs',
                  problem: 'In-house billing staff cost $40,000-$60,000 annually per employee, plus benefits (25-30%), software ($5,000-$15,000/year), training, and overhead. For a small practice, this represents 15-20% of total revenue.',
                  impact: 'A practice collecting $500,000 annually spends $75,000-$100,000 on billing—money that could fund patient care or practice growth.',
                  color: '#ef4444'
                },
                {
                  title: '2. Limited Coding Expertise',
                  problem: 'Small practices often rely on office staff who "learn as they go" with medical coding. Without certified coders, claim accuracy suffers. Coding errors lead to denials, underpayments, and compliance risks.',
                  impact: 'Coding mistakes cost small practices 5-10% of potential revenue annually. A $500K practice loses $25,000-$50,000 to avoidable errors.',
                  color: '#f97316'
                },
                {
                  title: '3. High Denial Rates',
                  problem: 'Small practices average 10-15% denial rates (vs. 3-5% for specialized billers). Common causes: missing information, coding errors, authorization issues, and untimely filing. Most denials never get worked due to limited staff.',
                  impact: '63% of denied claims are never reworked. With average reimbursement of $150/claim, a practice with 300 monthly claims loses $27,000-$40,500 annually.',
                  color: '#dc2626'
                },
                {
                  title: '4. Delayed Cash Flow',
                  problem: 'Without dedicated billing staff, claims sit for days before submission. Follow-up on unpaid claims is sporadic. Result: payments take 60-90 days instead of 20-30 days, creating constant cash flow pressure.',
                  impact: 'Delayed payments lock up $125,000-$175,000 in working capital for a $500K practice—money needed for payroll, rent, and operations.',
                  color: '#ea580c'
                },
                {
                  title: '5. Staff Turnover Chaos',
                  problem: 'Medical billing has 25-30% annual turnover. Each departure means lost knowledge, training new staff ($5,000-$15,000), productivity gaps (3-6 months), and increased errors during transitions.',
                  impact: 'A single billing staff turnover costs $10,000-$30,000 in direct and indirect costs, plus months of operational disruption.',
                  color: '#f59e0b'
                },
                {
                  title: '6. No Time for Patient Care',
                  problem: 'Physicians and office managers spend 10-15 hours weekly on billing issues—answering questions, reviewing reports, resolving problems, and managing staff. Time that should be spent on patients or practice growth.',
                  impact: 'Those 10-15 hours represent 25-37% of a physician\'s administrative time. Redirecting this time to patient care could add 2-3 patients daily, generating $50,000-$100,000 additional annual revenue.',
                  color: '#d97706'
                }
              ].map((problem, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#ffffff',
                  padding: '24px',
                  borderRadius: '12px',
                  border: `3px solid ${problem.color}`,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    <AlertCircle size={28} style={{ color: problem.color }} />
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      color: problem.color,
                      margin: 0
                    }}>
                      {problem.title}
                    </h3>
                  </div>
                  
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      THE PROBLEM:
                    </div>
                    <p style={{
                      fontSize: '0.9375rem',
                      lineHeight: '1.6',
                      color: '#4b5563',
                      margin: 0
                    }}>
                      {problem.problem}
                    </p>
                  </div>

                  <div style={{
                    backgroundColor: '#fef2f2',
                    padding: '12px',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${problem.color}`
                  }}>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: problem.color,
                      marginBottom: '6px'
                    }}>
                      FINANCIAL IMPACT:
                    </div>
                    <p style={{
                      fontSize: '0.9375rem',
                      lineHeight: '1.6',
                      color: '#374151',
                      margin: 0
                    }}>
                      {problem.impact}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Complete Solution Section */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '16px',
              color: '#111827'
            }}>
              Our Complete Solution for Small Practices
            </h2>
            <p style={{
              fontSize: '1.125rem',
              textAlign: 'center',
              color: '#6b7280',
              marginBottom: '48px',
              maxWidth: '48rem',
              margin: '0 auto 48px auto'
            }}>
              Everything you need for professional billing—at a fraction of in-house costs
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '24px'
            }}>
              {[
                {
                  icon: CheckCircle,
                  title: 'Expert Billing Team',
                  description: 'Certified coders (CPC, CCS) with 10+ years experience. Specialty-specific expertise. No turnover worries. Immediate productivity.',
                  benefit: 'Save $40K-$60K in salary costs'
                },
                {
                  icon: TrendingUp,
                  title: 'Maximize Collections',
                  description: 'Complete charge capture. Accurate coding (98% first-pass rate). Aggressive AR follow-up. 65-75% denial recovery.',
                  benefit: 'Increase revenue 15-25%'
                },
                {
                  icon: Clock,
                  title: 'Faster Payments',
                  description: '24-48 hour claim submission. Daily payment posting. Systematic follow-up on unpaid claims. Reduce days in AR by 30-40%.',
                  benefit: 'Improve cash flow $50K-$75K'
                },
                {
                  icon: Shield,
                  title: 'Reduce Denials',
                  description: 'Proactive eligibility verification. Accurate coding validation. Authorization management. Denial prevention strategies.',
                  benefit: 'Cut denials by 35-45%'
                },
                {
                  icon: BarChart3,
                  title: 'Real-Time Insights',
                  description: 'Live performance dashboards. Revenue by provider/service. Denial trends and patterns. Collections analytics.',
                  benefit: 'Data-driven decisions'
                },
                {
                  icon: Zap,
                  title: 'Technology Included',
                  description: 'Advanced billing software. EHR integration. Automated clearinghouse. No additional software costs.',
                  benefit: 'Save $5K-$15K annually'
                }
              ].map((solution, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#faf5ff',
                  padding: '28px',
                  borderRadius: '12px',
                  border: '2px solid #e9d5ff',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#7c3aed';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(124, 58, 237, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e9d5ff';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                >
                  <solution.icon size={40} style={{ color: '#7c3aed', marginBottom: '16px' }} />
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#111827'
                  }}>
                    {solution.title}
                  </h3>
                  <p style={{
                    fontSize: '0.9375rem',
                    lineHeight: '1.6',
                    color: '#4b5563',
                    marginBottom: '16px'
                  }}>
                    {solution.description}
                  </p>
                  <div style={{
                    backgroundColor: '#7c3aed',
                    color: '#ffffff',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    💰 {solution.benefit}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Financial Impact Section */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ecfdf5' }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '16px',
              color: '#111827'
            }}>
              Real Financial Impact Calculator
            </h2>
            <p style={{
              fontSize: '1.125rem',
              textAlign: 'center',
              color: '#059669',
              marginBottom: '40px'
            }}>
              Example: Small practice collecting $500,000 annually
            </p>

            {/* Cost Comparison Table */}
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
              marginBottom: '40px'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                backgroundColor: '#059669',
                color: '#ffffff',
                fontWeight: '600',
                fontSize: '1rem'
              }}>
                <div style={{ padding: '16px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Cost Category</div>
                <div style={{ padding: '16px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>In-House Billing</div>
                <div style={{ padding: '16px' }}>MDeRCM Outsourcing</div>
              </div>

              {[
                { category: 'Billing Staff Salary', inHouse: '$50,000', outsource: '$0' },
                { category: 'Benefits & Taxes (30%)', inHouse: '$15,000', outsource: '$0' },
                { category: 'Billing Software', inHouse: '$10,000', outsource: 'Included' },
                { category: 'Training & Onboarding', inHouse: '$5,000', outsource: '$0' },
                { category: 'Turnover Costs (annual)', inHouse: '$10,000', outsource: '$0' },
                { category: 'Office Space & Equipment', inHouse: '$5,000', outsource: '$0' },
                { category: 'RCM Service Fee (5%)', inHouse: '—', outsource: '$25,000' },
                { category: 'TOTAL ANNUAL COST', inHouse: '$95,000', outsource: '$25,000', highlight: true }
              ].map((row, idx) => (
                <div key={idx} style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                  backgroundColor: row.highlight ? '#dcfce7' : (idx % 2 === 0 ? '#f9fafb' : '#ffffff'),
                  fontWeight: row.highlight ? '700' : '400',
                  fontSize: row.highlight ? '1.125rem' : '0.9375rem',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <div style={{ padding: '12px 16px', borderRight: '1px solid #e5e7eb', color: row.highlight ? '#059669' : '#374151' }}>
                    {row.category}
                  </div>
                  <div style={{ padding: '12px 16px', borderRight: '1px solid #e5e7eb', textAlign: 'center', color: row.highlight ? '#dc2626' : '#374151' }}>
                    {row.inHouse}
                  </div>
                  <div style={{ padding: '12px 16px', textAlign: 'center', color: row.highlight ? '#059669' : '#374151' }}>
                    {row.outsource}
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom Line Box */}
            <div style={{
              backgroundColor: '#059669',
              color: '#ffffff',
              padding: '32px',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '1.125rem', marginBottom: '12px', opacity: 0.95 }}>
                💰 Annual Savings
              </div>
              <div style={{ fontSize: isMobile ? '2.5rem' : '3.5rem', fontWeight: 'bold', marginBottom: '8px' }}>
                $70,000
              </div>
              <div style={{ fontSize: '1rem', opacity: 0.9 }}>
                Plus $75,000-$125,000 in additional revenue from improved collections
              </div>
              <div style={{
                marginTop: '24px',
                padding: '16px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '8px',
                fontSize: '1.125rem',
                fontWeight: '600'
              }}>
                📈 Total Financial Benefit: $145,000 - $195,000 annually
              </div>
            </div>
          </div>
        </section>

        {/* Implementation Timeline */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '48px',
              color: '#111827'
            }}>
              Simple 4-Week Implementation
            </h2>

            <div style={{ position: 'relative' }}>
              {/* Timeline Line */}
              {!isMobile && (
                <div style={{
                  position: 'absolute',
                  left: '50%',
                  top: '0',
                  bottom: '0',
                  width: '2px',
                  backgroundColor: '#e5e7eb',
                  transform: 'translateX(-50%)'
                }}></div>
              )}

              {[
                {
                  week: 'Week 1-2',
                  title: 'Discovery & Setup',
                  tasks: ['Practice assessment', 'EHR integration setup', 'Staff training coordination', 'Account configuration'],
                  outcome: 'All systems ready'
                },
                {
                  week: 'Week 3',
                  title: 'Parallel Processing',
                  tasks: ['We process claims alongside your team', 'Quality assurance checks', 'Process refinement', 'Knowledge transfer'],
                  outcome: 'Smooth transition verified'
                },
                {
                  week: 'Week 4',
                  title: 'Full Transition',
                  tasks: ['We take over all billing', 'Daily monitoring', 'Quick issue resolution', 'Performance tracking begins'],
                  outcome: 'Full operation live'
                },
                {
                  week: 'Week 5+',
                  title: 'Optimization',
                  tasks: ['Continuous improvement', 'Regular reporting', 'Strategic recommendations', 'Ongoing support'],
                  outcome: 'Maximum results'
                }
              ].map((phase, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : (idx % 2 === 0 ? 'row' : 'row-reverse'),
                  alignItems: 'center',
                  marginBottom: '48px',
                  gap: '24px'
                }}>
                  <div style={{
                    flex: 1,
                    backgroundColor: '#faf5ff',
                    padding: '24px',
                    borderRadius: '12px',
                    border: '2px solid #e9d5ff'
                  }}>
                    <div style={{
                      display: 'inline-block',
                      backgroundColor: '#7c3aed',
                      color: '#ffffff',
                      padding: '6px 16px',
                      borderRadius: '20px',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      marginBottom: '12px'
                    }}>
                      {phase.week}
                    </div>
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      marginBottom: '12px',
                      color: '#111827'
                    }}>
                      {phase.title}
                    </h3>
                    <ul style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: '0 0 16px 0'
                    }}>
                      {phase.tasks.map((task, taskIdx) => (
                        <li key={taskIdx} style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          marginBottom: '8px',
                          fontSize: '0.9375rem',
                          color: '#4b5563'
                        }}>
                          <CheckCircle size={18} style={{ color: '#7c3aed', flexShrink: 0, marginTop: '2px' }} />
                          <span>{task}</span>
                        </li>
                      ))}
                    </ul>
                    <div style={{
                      backgroundColor: '#dcfce7',
                      color: '#15803d',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}>
                      ✓ {phase.outcome}
                    </div>
                  </div>

                  {!isMobile && (
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#7c3aed',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '1.125rem',
                      border: '4px solid #ffffff',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      zIndex: 1
                    }}>
                      {idx + 1}
                    </div>
                  )}

                  <div style={{ flex: 1 }}></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#f9fafb' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '48px',
              color: '#111827'
            }}>
              Frequently Asked Questions
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {[
                {
                  q: 'How much does medical billing outsourcing cost for small practices?',
                  a: 'Pricing is typically 4-6% of collections for small practices. For a practice collecting $500,000 annually, this means $20,000-$30,000 per year—compared to $95,000+ for in-house billing. You save 70-80% while getting better results.'
                },
                {
                  q: 'Will I lose control over my billing?',
                  a: 'No. You maintain full visibility with real-time dashboards, daily reports, and regular reviews. You approve all major decisions. Think of us as an extension of your team—we handle the work, you stay informed and in control.'
                },
                {
                  q: 'How long until I see results?',
                  a: 'Cost savings are immediate once transition is complete (Week 4). Revenue improvements typically show within 60-90 days as clean claims process faster and denials are recovered. Most practices see measurable ROI within the first quarter.'
                },
                {
                  q: 'What if I already have billing staff?',
                  a: 'We can work alongside your staff (hybrid model) or fully take over. Many practices redeploy billing staff to patient-facing roles where they add more value. We handle the technical billing work; your team focuses on patients.'
                },
                {
                  q: 'Do you work with my existing EHR/PM system?',
                  a: 'Yes. We integrate with all major EHR and practice management systems including Epic, Cerner, eClinicalWorks, Athena, Kareo, AdvancedMD, and dozens more. Integration is seamless and secure.'
                },
                {
                  q: 'What happens if there are billing problems or errors?',
                  a: 'We guarantee 98% claim accuracy. If errors occur, we fix them immediately at no additional cost. We also carry E&O insurance for your protection. Our quality assurance process catches issues before claims are submitted.'
                }
              ].map((faq, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#ffffff',
                  padding: '24px',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#7c3aed',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <span style={{ flexShrink: 0 }}>Q:</span>
                    <span>{faq.q}</span>
                  </h3>
                  <p style={{
                    fontSize: '1rem',
                    lineHeight: '1.75',
                    color: '#374151',
                    margin: 0,
                    paddingLeft: '24px'
                  }}>
                    <strong>A:</strong> {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{
          padding: isMobile ? '40px 20px' : '60px 40px',
          background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
          color: '#ffffff'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              marginBottom: '16px'
            }}>
              Ready to Save $70,000+ While Improving Collections?
            </h2>
            <p style={{
              fontSize: '1.125rem',
              marginBottom: '32px',
              opacity: 0.95
            }}>
              Join hundreds of small practices that have transformed their billing operations
            </p>

            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '16px',
              justifyContent: 'center',
              marginBottom: '32px'
            }}>
              <Link to="/free-trial" style={{
                textDecoration: 'none',
                backgroundColor: '#F26522',
                color: '#ffffff',
                padding: '16px 32px',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1.125rem',
                display: 'inline-block'
              }}>
                Get Free Practice Analysis →
              </Link>
              
              <a href="tel:+15103566069" style={{
                textDecoration: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: '#ffffff',
                padding: '16px 32px',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1.125rem',
                display: 'inline-block',
                border: '2px solid rgba(255,255,255,0.3)'
              }}>
                📞 +1 (510) 356-6069
              </a>
            </div>

            <p style={{
              fontSize: '0.9375rem',
              opacity: 0.9
            }}>
              ⭐ First 30 days FREE for new clients • No setup fees • No long-term contracts
            </p>
          </div>
        </section>

        {/* Related Services */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              textAlign: 'center',
              marginBottom: '32px',
              color: '#111827'
            }}>
              Related Solutions
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '20px'
            }}>
              {[
                { title: 'Independent Physician RCM', link: '/revenue-cycle-management-independent-physicians' },
                { title: 'Reduce Claim Denials', link: '/reduce-claim-denials-healthcare-billing' },
                { title: 'View Pricing Options', link: '/pricing' }
              ].map((link, idx) => (
                <Link
                  key={idx}
                  to={link.link}
                  style={{
                    padding: '20px',
                    backgroundColor: '#faf5ff',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    border: '2px solid #e9d5ff',
                    transition: 'all 0.3s ease',
                    display: 'block'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#7c3aed';
                    e.currentTarget.style.backgroundColor = '#f5f3ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e9d5ff';
                    e.currentTarget.style.backgroundColor = '#faf5ff';
                  }}
                >
                  <span style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#7c3aed'
                  }}>
                    {link.title} →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

      </main>
    </>
  );
};

export default MedicalBillingSmallPractices;